// src/pages/Connector/GoogleDrive/PendingUpdateModalForDrive.js
import React, { useEffect, useState, useCallback } from "react"; // Added useState, useCallback
import { Modal as MuiModal, Box, Button as MuiButton } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TableRow from '@tiptap/extension-table-row';

// Re-import Ant Design components if needed for the toolbar
import { Button as AntButton, Modal as AntModal, Input as AntInput, Space, Select, Dropdown, Menu } from 'antd';
import { DownOutlined } from '@ant-design/icons';

const { Option } = Select;

// Helper function to ensure URL has a protocol
const ensureUrlProtocol = (url) => {
  if (!url) return '';
  if (!/^https?:\/\//i.test(url) && !/^mailto:/i.test(url) && !/^tel:/i.test(url)) {
    return 'https://' + url;
  }
  return url;
};


export default function PendingUpdateModalForDrive({ 
    isOpen, 
    onClose, 
    onAccept, 
    onReject, 
    originalContentHTML, 
    pendingContentHTML,  
    documentUrl, 
    isLoadingAccept,
    isLoadingReject 
}) {
    const modalStyle = {
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '85vw' }, maxWidth: '1400px', maxHeight: '90vh',
        bgcolor: 'background.paper', boxShadow: 24, p: 3, borderRadius: '8px', outline: 'none',
        display: 'flex', flexDirection: 'column'
    };

    const commonExtensions = [
        StarterKit.configure({heading: {levels: [1,2,3,4,5,6]}}), 
        Link.configure({openOnClick: false, autolink: true, linkOnPaste: true, HTMLAttributes: {target: '_blank'}}), 
        Underline, 
        Table.configure({resizable: true}), 
        TableCell, TableHeader, TableRow
    ];

    // For "Your Current Version" - Read-only
    const originalEditor = useEditor({
        extensions: commonExtensions,
        content: originalContentHTML || "<p>No current content.</p>",
        editable: false,
        editorProps: { attributes: { class: 'tiptap-readonly-viewer' } },
    });

    // For "New Version from Google Drive" - Now Editable
    const pendingEditor = useEditor({
        extensions: commonExtensions,
        content: pendingContentHTML || "<p>No new content from web.</p>",
        editable: true, // MAKE THIS EDITABLE
        editorProps: { attributes: { class: 'tiptap-editor-area' } }, // Use standard editor class
    });
    
    // State for AntD Table Dialog within this modal
    const [isTableDialogOpen, setIsTableDialogOpen] = useState(false);
    const [tableRows, setTableRows] = useState(3);
    const [tableColumns, setTableColumns] = useState(3);


    useEffect(() => {
        if (originalEditor) {
            const currentContent = originalEditor.getHTML();
            const newContent = originalContentHTML || "<p>No current content.</p>";
            if (currentContent !== newContent) {
                originalEditor.commands.setContent(newContent);
            }
        }
    }, [originalContentHTML, originalEditor]);

    useEffect(() => {
        if (pendingEditor) {
            const currentContent = pendingEditor.getHTML();
            const newContent = pendingContentHTML || "<p>No new content from web.</p>";
             if (currentContent !== newContent) {
                pendingEditor.commands.setContent(newContent);
            }
        }
    }, [pendingContentHTML, pendingEditor]);

    const handleAcceptClick = () => {
        if (pendingEditor) {
            onAccept(pendingEditor.getHTML()); // Pass the current (potentially edited) content
        }
    };

    // --- Toolbar Logic for Pending Editor (copied from EditGoogleDriveFileContent) ---
    const setLink = useCallback(() => {
        if (!pendingEditor) return;
        const previousUrl = pendingEditor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl || '');
        if (url === null) return;
        if (url === '') {
            pendingEditor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        const validatedUrl = ensureUrlProtocol(url);
        pendingEditor.chain().focus().extendMarkRange('link').setLink({ href: validatedUrl }).run();
    }, [pendingEditor]);

    const insertTableDialog = useCallback(() => setIsTableDialogOpen(true), []);
    const handleInsertTable = useCallback(() => {
        if (!pendingEditor) return;
        const rows = parseInt(tableRows, 10);
        const cols = parseInt(tableColumns, 10);
        if (rows > 0 && cols > 0) {
            pendingEditor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
        }
        setIsTableDialogOpen(false);
    }, [pendingEditor, tableRows, tableColumns]);
    const closeTableDialog = useCallback(() => setIsTableDialogOpen(false), []);
    const handleTableRowsChange = useCallback((e) => setTableRows(Math.max(1, parseInt(e.target.value, 10) || 1)), []);
    const handleTableColumnsChange = useCallback((e) => setTableColumns(Math.max(1, parseInt(e.target.value, 10) || 1)), []);

    const handleHeadingChange = useCallback((value) => {
        if (!pendingEditor) return;
        if (value === 'paragraph') {
            pendingEditor.chain().focus().setParagraph().run();
        } else {
            const level = parseInt(value.slice(1), 10);
            if (level >= 1 && level <= 6) {
                pendingEditor.chain().focus().toggleHeading({ level }).run();
            }
        }
    }, [pendingEditor]);

    const handleAddColumn = useCallback(() => pendingEditor?.chain().focus().addColumnAfter().run(), [pendingEditor]);
    const handleAddRow = useCallback(() => pendingEditor?.chain().focus().addRowAfter().run(), [pendingEditor]);
    const handleDeleteColumn = useCallback(() => pendingEditor?.chain().focus().deleteColumn().run(), [pendingEditor]);
    const handleDeleteRow = useCallback(() => pendingEditor?.chain().focus().deleteRow().run(), [pendingEditor]);
    const handleDeleteTable = useCallback(() => pendingEditor?.chain().focus().deleteTable().run(), [pendingEditor]);
    
    const currentHeadingLevel = () => {
        if (!pendingEditor) return 'paragraph';
        for (let i = 1; i <= 6; i++) {
            if (pendingEditor.isActive('heading', { level: i })) return `h${i}`;
        }
        return 'paragraph';
    };

    const tableMenu = (
        <Menu>
          <Menu.Item key="insertTable" onClick={insertTableDialog}>Insert Table</Menu.Item>
          <Menu.Item key="addColumn" onClick={handleAddColumn} disabled={!pendingEditor || !pendingEditor.can().addColumnAfter()}>Add Column After</Menu.Item>
          <Menu.Item key="addRow" onClick={handleAddRow} disabled={!pendingEditor || !pendingEditor.can().addRowAfter()}>Add Row After</Menu.Item>
          <Menu.Item key="deleteColumn" onClick={handleDeleteColumn} disabled={!pendingEditor || !pendingEditor.can().deleteColumn()}>Delete Column</Menu.Item>
          <Menu.Item key="deleteRow" onClick={handleDeleteRow} disabled={!pendingEditor || !pendingEditor.can().deleteRow()}>Delete Row</Menu.Item>
          <Menu.Divider />
          <Menu.Item key="deleteTable" onClick={handleDeleteTable} disabled={!pendingEditor || !pendingEditor.can().deleteTable()}>Delete Table</Menu.Item>
        </Menu>
      );
    // --- End Toolbar Logic ---


    return (
        <MuiModal open={isOpen} onClose={onClose} aria-labelledby="gdrive-pending-update-title">
            <Box sx={modalStyle}>
                <h5 id="gdrive-pending-update-title" style={{marginTop:0, marginBottom: '5px'}}>Review Pending Update for:</h5>
                 <p style={{wordBreak: 'break-all', marginBottom: '15px', fontSize: '0.9em', background: '#f9f9f9', padding: '8px', borderRadius: '4px'}}>
                    {documentUrl}
                </p>
                <p style={{fontSize: '0.9em', color: '#555', marginBottom: '15px'}}>
                    The content from Google Drive has been updated. Review and edit the new version below. Choose to accept your edited version (overwriting your previous manual edits) or reject it (keeping your current version).
                </p>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: '15px', flexGrow: 1, overflow: 'hidden' }}>
                    {/* Current Version Panel (Read-Only) */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #ddd', borderRadius: '4px' }}>
                        <h6 style={{ padding: '10px 12px', backgroundColor: '#f0f0f0', margin: 0, borderBottom: '1px solid #ddd', fontSize: '0.9rem' }}>Your Current Version (Manually Edited)</h6>
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: '0px', margin:'5px'}}>
                            {originalEditor ? <EditorContent editor={originalEditor} className="tiptap-viewer-container"/> : <p>Loading current content...</p>}
                        </Box>
                    </Box>

                    {/* New Version Panel (Editable) */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #a0d3a0', borderRadius: '4px' }}>
                        <h6 style={{ padding: '10px 12px', backgroundColor: '#e6ffed', margin: 0, borderBottom: '1px solid #a0d3a0', fontSize: '0.9rem' }}>
                            New Version from Google Drive (Editable)
                        </h6>
                        {/* Toolbar for Pending Editor */}
                        {pendingEditor && (
                            <div className="sticky-toolbar" style={{ flexShrink: 0, borderBottom: '1px solid #a0d3a0', padding: '5px', background: '#f8f9fa' }}>
                                <div className="toolbar" style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', alignItems: 'center' }}>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().toggleBold().run()} className={pendingEditor.isActive('bold') ? 'is-active' : ''} title="Bold"><i className="fa fa-bold"></i></AntButton>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().toggleItalic().run()} className={pendingEditor.isActive('italic') ? 'is-active' : ''} title="Italic"><i className="fa fa-italic"></i></AntButton>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().toggleUnderline().run()} className={pendingEditor.isActive('underline') ? 'is-active' : ''} title="Underline"><i className="fa fa-underline"></i></AntButton>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().toggleStrike().run()} className={pendingEditor.isActive('strike') ? 'is-active' : ''} title="Strike"><i className="fa fa-strikethrough"></i></AntButton>
                                    <AntButton size="small" onClick={setLink} className={pendingEditor.isActive('link') ? 'is-active' : ''} title="Link"><i className="fa fa-link"></i></AntButton>
                                    <Select size="small" value={currentHeadingLevel()} style={{ width: 100 }} onChange={handleHeadingChange}>
                                        <Option value="paragraph">Paragraph</Option>
                                        {[1,2,3,4,5,6].map(level => <Option key={level} value={`h${level}`}>H{level}</Option>)}
                                    </Select>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().toggleBulletList().run()} className={pendingEditor.isActive('bulletList') ? 'is-active' : ''} title="Bullet List"><i className="fa fa-list-ul"></i></AntButton>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().toggleOrderedList().run()} className={pendingEditor.isActive('orderedList') ? 'is-active' : ''} title="Ordered List"><i className="fa fa-list-ol"></i></AntButton>
                                    <Dropdown overlay={tableMenu} trigger={['click']} overlayStyle={{ zIndex: 1600 }}>
                                        <AntButton size="small"><i className="fa fa-table" style={{ marginRight: '4px'}}></i> Table <DownOutlined /></AntButton>
                                    </Dropdown>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().undo().run()} disabled={!pendingEditor.can().undo()} title="Undo"><i className="fa fa-undo"></i></AntButton>
                                    <AntButton size="small" onClick={() => pendingEditor.chain().focus().redo().run()} disabled={!pendingEditor.can().redo()} title="Redo"><i className="fa fa-repeat"></i></AntButton>
                                </div>
                            </div>
                        )}
                        <Box sx={{ flexGrow: 1, overflowY: 'auto', padding: '0px', margin:'5px'}}>
                            {pendingEditor ? <EditorContent editor={pendingEditor} className="tiptap-editor-area"/> : <p>Loading new content...</p>}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', flexShrink: 0, gap: '10px' }}>
                    <MuiButton variant="outlined" onClick={onClose}>Close Review</MuiButton>
                    <Box sx={{display: 'flex', gap: '10px'}}>
                        <MuiButton 
                            variant="outlined" 
                            color="error" 
                            onClick={onReject} 
                            disabled={isLoadingReject || isLoadingAccept}
                            startIcon={isLoadingReject ? <i className="fa fa-spinner fa-spin"/> : <i className="fa fa-times-circle"/>}
                        >
                            Reject New Version
                        </MuiButton>
                        <MuiButton 
                            variant="contained" 
                            color="primary" 
                            onClick={handleAcceptClick} // Use the new handler
                            disabled={isLoadingAccept || isLoadingReject || !pendingEditor}
                            startIcon={isLoadingAccept ? <i className="fa fa-spinner fa-spin"/> : <i className="fa fa-check-circle"/>}
                        >
                          Accept (Edited) Version
                        </MuiButton>
                    </Box>
                </Box>
                {/* AntD Modal for Table Insertion, if using AntD components in toolbar */}
                <AntModal title="Insert Table" open={isTableDialogOpen} onOk={handleInsertTable} onCancel={closeTableDialog} okText="Insert" cancelText="Cancel" zIndex={1700}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <AntInput type="number" addonBefore="Rows" value={tableRows} onChange={handleTableRowsChange} min={1} />
                        <AntInput type="number" addonBefore="Columns" value={tableColumns} onChange={handleTableColumnsChange} min={1} />
                    </Space>
                </AntModal>

                 <style jsx global>{`
                    .tiptap-readonly-viewer .ProseMirror { padding: 0.75rem; outline: none; }
                    .tiptap-editor-area .ProseMirror { padding: 0.75rem; outline: none; } /* Style for editable area */
                    .tiptap-viewer-container, .tiptap-editor-area { height: 100%; } 
                    .ProseMirror-focused {outline: none;}
                    .ProseMirror p { margin-bottom: 0.75rem; line-height: 1.6; }
                    .ProseMirror ul, .ProseMirror ol { padding-left: 1.75rem; margin-bottom: 0.75rem; }
                    .ProseMirror li p { margin-bottom: 0.2rem; }
                    .ProseMirror a { color: #0d6efd; text-decoration: underline; cursor: pointer; }
                    .ProseMirror h1, .ProseMirror h2, .ProseMirror h3, .ProseMirror h4, .ProseMirror h5, .ProseMirror h6 { margin-top: 1rem; margin-bottom: 0.5rem; line-height: 1.4; font-weight: 500; }
                    .ProseMirror table { border-collapse: collapse; margin: 0.75rem 0; width: 100%; table-layout: fixed; }
                    .ProseMirror th, .ProseMirror td { border: 1px solid #dee2e6; padding: 0.4rem 0.5rem; vertical-align: top; }
                    .ProseMirror th { font-weight: bold; text-align: left; background-color: #f1f3f5; }
                    .toolbar button, .toolbar .ant-select-sm { margin-right: 4px !important; }
                    .toolbar button.is-active { background-color: #e0e0e0; }
                    /* Ensure AntD dropdowns and modals appear above MUI modal if necessary */
                    .ant-select-dropdown, .ant-dropdown-menu { z-index: 1650 !important; } 
                    .ant-modal-wrap { z-index: 1700 !important; }
                `}</style>
            </Box>
        </MuiModal>
    );
}